import { NextRouter } from 'next/router';

/**
 * Converts Next.js router pathname with dynamic segments to actual pathname
 * This function resolves dynamic route parameters (e.g., [id], [...slugs])
 * by replacing them with actual values from router.query
 *
 * @param router - Next.js router instance
 * @returns resolved pathname string
 *
 * @example
 * // Input: pathname: "/[[...slugs]]", query: { slugs: ["_next", "data", "mstrXHiOToyQavhA6QfGk", "en-us", "avatar", "resources.json"] }
 * // Output: "/en-us/avatar/resources"
 *
 * @example
 * // Input: pathname: "/[[...slugs]]", query: { slugs: ["_next", "data", "-ktlDoO_DsWarqHSh1Wml", "reference", "engine", "classes", "Object.json"] }
 * // Output: "/reference/engine/classes/Object"
 */
export const getPathName = (router: NextRouter): string => {
  let resolvedPathname = router.pathname;
  const queryEntries = Object.entries(router.query);

  // Handle Next.js data requests
  const slugs = queryEntries.find(([key]) => key === 'slugs')?.[1];
  if (Array.isArray(slugs) && slugs.length >= 3 && slugs[0] === '_next' && slugs[1] === 'data') {
    // This is a data request - extract the actual page path
    const pageSlugs = slugs
      .slice(3) // Skip _next, data, and buildId (first 3 elements)
      .map((slug) => slug.replace(/\.json$/, '')); // Remove .json extension
    return `/${pageSlugs.join('/')}`;
  }

  // Early return for empty query - just clean up optional catch-all patterns
  if (queryEntries.length === 0) {
    return resolvedPathname.replace(/\[\[\.\.\.\w+\]\]/, '');
  }

  // Process each query parameter to resolve dynamic segments
  queryEntries.forEach(([paramName, paramValue]) => {
    if (paramValue instanceof Array) {
      // https://nextjs.org/docs/app/api-reference/file-conventions/dynamic-routes
      // Handle catch-all routes (both optional and required)
      const optionalCatchAllPattern = `[[...${paramName}]]`; // [[...slugs]]
      const requiredCatchAllPattern = `[...${paramName}]`; // [...slugs]

      if (resolvedPathname.includes(optionalCatchAllPattern)) {
        resolvedPathname = resolvedPathname.replace(optionalCatchAllPattern, paramValue.join('/'));
      } else if (resolvedPathname.includes(requiredCatchAllPattern)) {
        resolvedPathname = resolvedPathname.replace(requiredCatchAllPattern, paramValue.join('/'));
      }
    } else if (paramValue != null) {
      const parameterPattern = `[${paramName}]`; // [slug]
      if (resolvedPathname.includes(parameterPattern)) {
        resolvedPathname = resolvedPathname.replace(parameterPattern, paramValue as string);
      }
    }
  });

  return resolvedPathname;
};

export default {
  getPathName,
};
